import React, { useState, useMemo, useEffect } from "react";
import { usePodcast } from '../../context/PodcastContext';
import {
    Card,
    CardContent,
    Typography,
    Button,
    Box,
    Stack,
    Divider,
    Alert,
    Radio,
    RadioGroup,
    LinearProgress,
    FormControlLabel,
    Checkbox,
    TextField,
} from "@mui/material";
import {
    DirectionsWalk,
    SelfImprovement,
    Headphones,
    EditNote,
    Favorite,
    CampaignRounded,
    CheckCircle,
    ArrowForward,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { trackCTA } from "../../service/usercta";
import { getWellnessCampaignStatus, saveWellnessCampaign } from "../../service/wellnessCampaign";
// import { saveWellnessCampaign } from "../../service/wellnessCampaign";

/* ─────────────────────────────────────────────────────────────────────────
   Design tokens — same gradient family used across the app's campaign
   cards (Campaign.jsx / Selfcarecampaign.jsx) so this reads as part of the
   same product.
   ───────────────────────────────────────────────────────────────────────── */
// const tokens = {
//     gradient: "linear-gradient(135deg, #2f6fd0, #6d5bd0)",
//     primary: "#2f6fd0",
//     primaryLight: "#E8F0FD",
//     accent: "#6d5bd0",
//     border: "#E8EAF0",
//     textSecondary: "#5f6368",
// };
const tokens = {
    gradient: "linear-gradient(135deg, #2f9e6f, #6d5bd0)",
    primary: "#2f9e6f",
    primaryLight: "#E7F5EE",
    accent: "#6d5bd0",
    border: "#E8EAF0",
    textSecondary: "#5f6368",
};

// Same hero image for every day, per the campaign deck
const HERO_IMAGE = "https://miro.medium.com/1*Ta1VQB04RdQwIQFNcn_23g.jpeg";

// Define the real campaign window using actual dates
const CAMPAIGN_START = new Date(2026, 7, 24); // Month is 0-indexed: 7 = August

// Calendar date → day number (1-6). 29th & 30th (Sat/Sun) are skipped.
const DATE_TO_DAY = { 24: 1, 25: 2, 26: 3, 27: 4, 28: 5, 31: 6 };

const getTodayDateOnly = () => {
    // Dev-only override: ?debugDate=2026-08-29 lets you simulate "today"
    // without touching the system clock. Stripped out of prod builds.
    if (import.meta.env.DEV) {
        const debugDateParam = new URLSearchParams(window.location.search).get("debugDate");
        if (debugDateParam) {
            const parsed = new Date(debugDateParam + "T00:00:00");
            if (!isNaN(parsed)) return parsed;
        }
    }
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
};

const DAY_ORDER = [1, 2, 3, 4, 5, 6];

const DAY_META = {
    1: { label: "Day 1 · Mon 24 Aug", short: "Your Wellness Nudge", icon: <DirectionsWalk fontSize="small" /> },
    2: { label: "Day 2 · Tue 25 Aug", short: "A Mindful 60-Second", icon: <SelfImprovement fontSize="small" /> },
    3: { label: "Day 3 · Wed 26 Aug", short: "Pause & Listen", icon: <Headphones fontSize="small" /> },
    4: { label: "Day 4 · Thu 27 Aug", short: "Put it Into Words", icon: <EditNote fontSize="small" /> },
    5: { label: "Day 5 · Fri 28 Aug", short: "Pass on Some Good", icon: <Favorite fontSize="small" /> },
    6: { label: "Day 6 · Mon 31 Aug", short: "Say it Out Loud", icon: <CampaignRounded fontSize="small" /> },
};

const JOURNAL_PROMPTS = [
    "What's one thing I'm proud of today?",
    "What made me smile today?",
    "What's one thing I need more of right now?",
    "What distracted me the most today?",
    "What's one thing I can do for myself today?",
];

const FINAL_POLL_OPTIONS = [
    { id: "wellness_nudge", label: "Your Wellness Nudge" },
    { id: "mindful_60", label: "A Mindful 60-Second" },
    { id: "pause_listen", label: "Pause & Listen: Podcast on Prevention to Intervention" },
    { id: "words", label: "Put it Into Words" },
    { id: "pass_it_on", label: "Pass on Some Good" },
];

const REMINDERS = [
    "Movement, reflection, and rest are all forms of wellness.",
    "You don't have to do everything at once. Find what works for you.",
    "Keep making space for yourself, one day at a time.",
];

const WellnessCampaign = () => {
    const podcastCtx = usePodcast();
    const onOpenPodcast = podcastCtx?.handleListenNow;
    const podcastPlayedMs = podcastCtx?.podcastPlayedMs ?? 0;
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const accountId = user.account_id || user.accountId;
    const userId = user.id;
    const urlDayOverride = new URLSearchParams(window.location.search).get("wday");
    const parsedUrlDay = urlDayOverride ? Number(urlDayOverride) : null;
    const validUrlDay = (parsedUrlDay >= 1 && parsedUrlDay <= 6) ? parsedUrlDay : null;
    const todayDayNum = validUrlDay ?? (DATE_TO_DAY[getTodayDateOnly().getDate()] ?? null);

    const [completedDays, setCompletedDays] = useState({});
    // const devParams = import.meta.env.DEV
    //     ? new URLSearchParams(window.location.search)
    //     : null;

    // const parsedUrlDay = devParams ? Number(devParams.get("wday")) : null;
    // const forcedDay = (parsedUrlDay >= 1 && parsedUrlDay <= 6) ? parsedUrlDay : null;

    // const parsedSimDay = devParams ? Number(devParams.get("simday")) : null;
    // const simDay = (parsedSimDay >= 1 && parsedSimDay <= 6) ? parsedSimDay : null;

    // const todayDayNum = simDay ?? (DATE_TO_DAY[getTodayDateOnly().getDate()] ?? null);

    // const [completedDays, setCompletedDays] = useState(() => {
    //     if (!devParams) return {};
    //     const seed = devParams.get("complete");
    //     if (!seed) return {};
    //     const seeded = {};
    //     seed.split(",").forEach((d) => {
    //         const n = Number(d.trim());
    //         if (n >= 1 && n <= 6) seeded[n] = true;
    //     });
    //     return seeded;
    // });

    // Local UI state for the in-card challenges
    const [podcastFeedback, setPodcastFeedback] = useState(null);
    const [journalPrompt, setJournalPrompt] = useState(null);
    const [journalAnswer, setJournalAnswer] = useState("");
    const [pollSelections, setPollSelections] = useState([]);

    useEffect(() => {
        if (!userId || !accountId) return;

        getWellnessCampaignStatus(userId, accountId)
            .then((response) => {
                if (response?.success && Array.isArray(response.data)) {
                    const serverDays = {};

                    response.data.forEach((day) => {
                        serverDays[day.day_number] = day.completed;
                    });

                    setCompletedDays((prev) => ({
                        ...prev,
                        ...serverDays,
                    }));
                }
            })
            .catch((error) => {
                console.error("Failed to fetch campaign status:", error);
            });
    }, [userId, accountId]);

    const isWithinCampaignMonth = (() => {
        if (validUrlDay) return true;
        const now = getTodayDateOnly();
        return now.getFullYear() === CAMPAIGN_START.getFullYear() &&
            now.getMonth() === CAMPAIGN_START.getMonth();
    })();

    const markDayComplete = (day, ctaName) => {
        trackCTA(ctaName, JSON.parse(localStorage.getItem("user") || "{}"));
        setCompletedDays((prev) => ({ ...prev, [day]: true }));
    };

    const resetLocalChallengeState = () => {
        setPodcastFeedback(null);
        setJournalPrompt(null);
        setJournalAnswer("");
        setPollSelections([]);
    };

    const handleCompleteAndAdvance = (day, ctaName) => {
        markDayComplete(day, ctaName);
        resetLocalChallengeState();
    };

    const handleSaveCampaign = async (dayNumber, data = {}) => {
        if (!accountId) {
            console.error("Missing accountId — cannot save campaign response");
            return;
        }

        try {
            const payload = {
                user_id: userId || 0,
                account_id: accountId,
                day_number: dayNumber,
                response_data: data, // whatever shape is relevant to this day
                completed: true,
            };

            const response = await saveWellnessCampaign(payload);
            console.log("Saved:", response);
        } catch (error) {
            console.error("Failed to save campaign:", error);
        }
    };

    // Campaign only shows once the window has opened
    const campaignStarted = isWithinCampaignMonth && todayDayNum !== null;
    // const campaignStarted = forcedDay ? true : (isWithinCampaignMonth && todayDayNum !== null);

    // Clamp so we never look past Day 6 for "what should be unlocked"
    const targetDay = Math.min(todayDayNum ?? 0, 6);

    const firstIncompleteDay = useMemo(
        () => DAY_ORDER.find((d) => d <= targetDay && !completedDays[d]),
        [completedDays, targetDay]
    );

    if (!campaignStarted) return null;


    if (!firstIncompleteDay) {
        if (targetDay === 6) {
            return <CongratsCard />;
        }
        return <WaitingCard nextDay={DAY_ORDER[DAY_ORDER.indexOf(targetDay) + 1]} />;
    }

    const activeDay = firstIncompleteDay;
    const isCatchUp = activeDay < todayDayNum;

    const goToQuickActions = () => {
        handleSaveCampaign(1, { action_completed: "quick_actions" });
        handleCompleteAndAdvance(1, "campaign_wellness_day1_quickactions");
        navigate("/care", { state: { showQuickActions: true } });
    };

    const goToDailyBreathing = () => {
        handleSaveCampaign(2, { action_completed: "daily_breathing" });
        handleCompleteAndAdvance(2, "campaign_wellness_day2_breathing");
        navigate("/daily-breathing");
    };

    const goToGratitudeJournal = () => {
        handleSaveCampaign(5, { action_completed: "gratitude_journal" });
        handleCompleteAndAdvance(5, "campaign_wellness_day5_gratitude");
        navigate("/gratitude-checkin");
    };

    return (
        <Box sx={{ maxWidth: 560, mx: "auto", px: { xs: 1, sm: 0 } }}>
            <Card
                elevation={0}
                sx={{
                    borderRadius: "16px",
                    border: `1px solid ${tokens.border}`,
                    overflow: "hidden",
                    transition: "box-shadow 0.2s ease",
                    "&:hover": { boxShadow: "0 8px 32px rgba(47,111,208,0.10)" },
                    animation: "wcCardIn 0.45s ease-out both",
                    "@keyframes wcCardIn": {
                        from: { opacity: 0, transform: "translateY(18px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                }}
            >
                {/* ── Hero image ── */}
                <Box sx={{ position: "relative", height: { xs: 190, sm: 230 }, overflow: "hidden" }}>
                    <Box
                        component="img"
                        src={HERO_IMAGE}
                        alt={DAY_META[activeDay]?.short || "Wellness"}
                        loading="lazy"
                        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                    <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.68) 100%)" }} />

                    <Box sx={{
                        position: "absolute", inset: 0, pointerEvents: "none",
                        background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.07) 50%, transparent 60%)",
                        backgroundSize: "200% 100%",
                        animation: "wcHeroShimmer 3.5s ease-in-out infinite",
                        "@keyframes wcHeroShimmer": {
                            "0%": { backgroundPosition: "200% 0" },
                            "100%": { backgroundPosition: "-200% 0" },
                        },
                    }} />

                    {/* Campaign badge — top left */}
                    <Box sx={{
                        position: "absolute", top: 14, left: 14,
                        display: "inline-flex", alignItems: "center", gap: 0.5,
                        bgcolor: "rgba(255,255,255,0.18)", backdropFilter: "blur(8px)",
                        border: "1px solid rgba(255,255,255,0.32)",
                        borderRadius: "20px", px: 1.25, py: 0.4,
                    }}>
                        <SelfImprovement sx={{ fontSize: 12, color: "#fff" }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#fff", letterSpacing: "0.07em", textTransform: "uppercase", fontSize: "0.6rem" }}>
                            Campaign
                        </Typography>
                    </Box>

                    {/* Day progress pills — top right */}
                    <Stack direction="row" spacing={0.5} sx={{ position: "absolute", top: 16, right: 14 }}>
                        {DAY_ORDER.map((d) => (
                            <Box
                                key={d}
                                title={DAY_META[d].label}
                                sx={{
                                    width: completedDays[d] || d === activeDay ? 18 : 6,
                                    height: 6,
                                    borderRadius: 3,
                                    transition: "all 0.3s ease",
                                    bgcolor: completedDays[d] ? tokens.primary : d === activeDay ? "#fff" : "rgba(255,255,255,0.35)",
                                    ...(d === activeDay && !completedDays[d] && {
                                        animation: "wcPillGlow 1.8s ease-in-out infinite",
                                        "@keyframes wcPillGlow": {
                                            "0%,100%": { boxShadow: "0 0 0 0 rgba(255,255,255,0.6)" },
                                            "50%": { boxShadow: "0 0 0 4px rgba(255,255,255,0)" },
                                        },
                                    }),
                                }}
                            />
                        ))}
                    </Stack>

                    {/* Bottom overlay: campaign name + day label */}
                    <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, px: 2, pb: 2, pt: 4 }}>
                        <Typography variant="h6" fontWeight={700} sx={{ color: "#fff", lineHeight: 1.2, textShadow: "0 2px 10px rgba(0,0,0,0.55)", mb: 0.3 }}>
                            Wellness, One Day at a Time
                        </Typography>
                        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.88)", textShadow: "0 1px 4px rgba(0,0,0,0.4)", fontWeight: 500 }}>
                            {DAY_META[activeDay].label} · {DAY_META[activeDay].short}
                        </Typography>
                    </Box>
                </Box>

                <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: 2.5 } }}>
                    {/* ── Catch-up banner ── */}
                    {isCatchUp && (
                        <Alert severity="info" sx={{ mb: 2, borderRadius: "10px" }}>
                            Today's {DAY_META[todayDayNum] ? `Day ${todayDayNum}` : "challenge"} is waiting, but
                            first finish {DAY_META[activeDay].label} — {DAY_META[activeDay].short}.
                        </Alert>
                    )}

                    {/* ── Day-specific content ── */}
                    {activeDay === 1 && (
                        <NudgeChallenge
                            title="Your Wellness Nudge"
                            body="After lunch today, step away from your screen and take a 10-15 minute walk. Sitting for long periods can leave us feeling physically and mentally fatigued — today, step away from your screen and spend a few minutes walking, stretching, or simply moving around."
                            challenge="Step away from your screen and take a 10-15 minute walk."
                            buttonLabel="Go to Quick Actions"
                            onGo={goToQuickActions}
                        />
                    )}

                    {activeDay === 2 && (
                        <NudgeChallenge
                            title="A Mindful 60-Second"
                            body="Before moving on to your next task, pause for 60 seconds. Sit comfortably, slow down your breathing, and bring your attention to the present moment."
                            challenge="Pause → Breathe → Reset → Continue. Give yourself one minute before you give your attention to the next thing."
                            buttonLabel="Go to Daily Breathing"
                            onGo={goToDailyBreathing}
                        />
                    )}

                    {/* {activeDay === 3 && (
                        <PodcastChallenge
                            feedback={podcastFeedback}
                            podcastPlayedMs={podcastPlayedMs}
                            onOpenPodcast={onOpenPodcast}
                            onSubmitFeedback={(value) => {
                                setPodcastFeedback(value);
                                handleSaveCampaign(3, { podcast_feedback: value });
                                handleCompleteAndAdvance(3, "campaign_wellness_day3_podcast");
                            }}
                        />
                    )} */}
                    {activeDay === 3 && (
                        <PodcastChallenge
                            feedback={podcastFeedback}
                            podcastPlayedMs={podcastPlayedMs}
                            onOpenPodcast={onOpenPodcast}

                            onPodcastStarted={() => {
                                handleSaveCampaign(
                                    3,
                                    {
                                        action: "listen_to_podcast",
                                    },
                                    false
                                );
                            }}

                            onSubmitFeedback={(value) => {
                                setPodcastFeedback(value);

                                handleSaveCampaign(
                                    3,
                                    {
                                        action: "listen_to_podcast",
                                        podcast_feedback: value,
                                    },
                                    true
                                );

                                handleCompleteAndAdvance(
                                    3,
                                    "campaign_wellness_day3_podcast"
                                );
                            }}
                        />
                    )}

                    {activeDay === 4 && (
                        <JournalChallenge
                            prompt={journalPrompt}
                            setPrompt={setJournalPrompt}
                            answer={journalAnswer}
                            setAnswer={setJournalAnswer}
                            onSubmit={() => {
                                handleSaveCampaign(4, {
                                    journal_prompt: journalPrompt,
                                    journal_answer: journalAnswer,
                                });
                                handleCompleteAndAdvance(4, "campaign_wellness_day4_journal");
                            }}
                        />
                    )}

                    {activeDay === 5 && (
                        <NudgeChallenge
                            title="Pass on Some Good"
                            body="Take a moment to think about someone or something you're grateful for today. 💛 It could be a person who supported you, a small moment that made your day better, or simply something you often take for granted."
                            challenge="Pause. Think of it. Put it into words. Spend a few minutes reflecting on what you're grateful for today."
                            buttonLabel="Go to Gratitude Journal"
                            onGo={goToGratitudeJournal}
                        />
                    )}

                    {activeDay === 6 && (
                        <FinalPollChallenge
                            selections={pollSelections}
                            setSelections={setPollSelections}
                            onSubmit={() => {
                                handleSaveCampaign(6, { final_poll: pollSelections });
                                handleCompleteAndAdvance(6, "campaign_wellness_day6_poll");
                            }}
                        />
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────────────────
   Days 1, 2, 5 — challenge text + a single redirect CTA
   ───────────────────────────────────────────────────────────────────────── */
const NudgeChallenge = ({ title, body, challenge, buttonLabel, onGo }) => (
    <Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
            {title}
        </Typography>
        <Typography variant="body2" sx={{ color: tokens.textSecondary, lineHeight: 1.6, mb: 1.5 }}>
            {body}
        </Typography>
        <Box sx={{
            p: 1.5, borderRadius: "10px", bgcolor: tokens.primaryLight,
            border: `1px solid ${tokens.border}`, mb: 2,
        }}>
            <Typography variant="body2" sx={{ color: "#111827", fontWeight: 600 }}>
                Your challenge:
            </Typography>
            <Typography variant="body2" sx={{ color: tokens.textSecondary, mt: 0.25 }}>
                {challenge}
            </Typography>
        </Box>
        <Button
            variant="contained"
            endIcon={<ArrowForward fontSize="small" />}
            onClick={onGo}
            sx={{
                background: tokens.gradient,
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "none",
                animation: "wcCtaPulse 2.4s ease-in-out infinite",
                "@keyframes wcCtaPulse": {
                    "0%,100%": { boxShadow: "0 0 0 0 rgba(47,111,208,0.45)" },
                    "50%": { boxShadow: "0 0 0 7px rgba(47,111,208,0)" },
                },
            }}
        >
            {buttonLabel}
        </Button>
    </Box>
);

/* ─────────────────────────────────────────────────────────────────────────
   Day 3 — Pause & Listen (podcast)
   ───────────────────────────────────────────────────────────────────────── */
const PODCAST_REQUIRED_MS = 10 * 60 * 1000; // 10 minutes, per the campaign deck

const PodcastChallenge = ({ feedback, onOpenPodcast, onSubmitFeedback, onPodcastStarted, podcastPlayedMs = 0 }) => {
    const podcastStarted = podcastPlayedMs > 0;
    const podcastEligible = podcastPlayedMs >= PODCAST_REQUIRED_MS;
    const remainingSec = Math.max(0, Math.ceil((PODCAST_REQUIRED_MS - podcastPlayedMs) / 1000));
    const mins = Math.floor(remainingSec / 60);
    const secs = remainingSec % 60;
    const progress = Math.min(100, (podcastPlayedMs / PODCAST_REQUIRED_MS) * 100);

    return (
        <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Pause &amp; Listen
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                Prevention to Intervention
            </Typography>

            {!podcastStarted && (
                <>
                    <Typography variant="body2" sx={{ color: tokens.textSecondary, mb: 2 }}>
                        Take out a few minutes for your mental well-being today. Listen to the "Prevention to
                        Intervention" podcast for at least 10 minutes — as you listen, notice one idea or thought
                        that resonates with you and take a moment to reflect on it.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => {
                            onOpenPodcast();
                            onPodcastStarted();
                        }}
                        sx={{
                            background: tokens.gradient, borderRadius: "8px", textTransform: "none", fontWeight: 600, boxShadow: "none",
                            animation: "wcCtaPulse2 2.4s ease-in-out infinite",
                            "@keyframes wcCtaPulse2": {
                                "0%,100%": { boxShadow: "0 0 0 0 rgba(47,111,208,0.45)" },
                                "50%": { boxShadow: "0 0 0 7px rgba(47,111,208,0)" },
                            },
                        }}
                    >
                        Listen to the Podcast
                    </Button>
                </>
            )}

            {podcastStarted && !podcastEligible && (
                <>
                    <Typography variant="body2" sx={{ color: tokens.textSecondary, mb: 1.5 }}>
                        Keep listening — feedback unlocks in <strong>{mins}:{String(secs).padStart(2, "0")}</strong>
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            mb: 2, height: 6, borderRadius: 3,
                            bgcolor: tokens.primaryLight,
                            "& .MuiLinearProgress-bar": { background: tokens.gradient },
                        }}
                    />
                    <Button
                        variant="outlined"
                        onClick={onOpenPodcast}
                        sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, borderColor: tokens.primary, color: tokens.primary }}
                    >
                        Continue Listening
                    </Button>
                </>
            )}

            {podcastEligible && (
                <>
                    <Typography variant="body2" sx={{ color: tokens.textSecondary, mb: 1 }}>
                        Let us know if you found the podcast
                    </Typography>
                    <RadioGroup
                        value={feedback || ""}
                        onChange={(e) => onSubmitFeedback(e.target.value)}
                    >
                        {["Helpful", "Somewhat Helpful", "Not for Me"].map((label) => (
                            <FormControlLabel
                                key={label}
                                value={label}
                                control={<Radio size="small" sx={{ color: tokens.primary, "&.Mui-checked": { color: tokens.primary } }} />}
                                label={<Typography variant="body2">{label}</Typography>}
                            />
                        ))}
                    </RadioGroup>
                </>
            )}
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────────────────
   Day 4 — Put it Into Words (journal prompt + free text)
   ───────────────────────────────────────────────────────────────────────── */
const JournalChallenge = ({ prompt, setPrompt, answer, setAnswer, onSubmit }) => (
    <Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Put it Into Words!
        </Typography>
        <Typography variant="body2" sx={{ color: tokens.textSecondary, mb: 1.5 }}>
            Take 3 minutes to journal. You don't need to write a lot — choose one prompt and write whatever
            comes to mind. Pick one prompt, write honestly, there are no right answers.
        </Typography>

        <RadioGroup
            value={prompt ?? ""}
            onChange={(e) => setPrompt(e.target.value)}
        >
            {JOURNAL_PROMPTS.map((p) => (
                <FormControlLabel
                    key={p}
                    value={p}
                    control={<Radio size="small" sx={{ color: tokens.primary, "&.Mui-checked": { color: tokens.primary } }} />}
                    label={<Typography variant="body2" sx={{ color: "text.secondary" }}>{p}</Typography>}
                    sx={{ mb: 0.25, alignItems: "flex-start", "& .MuiRadio-root": { pt: 0.25 } }}
                />
            ))}
        </RadioGroup>

        <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Share your thoughts..."
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            disabled={!prompt}
            sx={{
                mt: 1.5, mb: 2,
                "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "& fieldset": { borderColor: tokens.border },
                },
            }}
        />

        <Button
            variant="contained"
            disabled={!prompt || !answer.trim()}
            onClick={onSubmit}
            sx={{
                background: tokens.gradient, borderRadius: "8px", textTransform: "none", fontWeight: 600, boxShadow: "none",
            }}
        >
            Submit
        </Button>
    </Box>
);

/* ─────────────────────────────────────────────────────────────────────────
   Day 6 — Say it Out Loud (final poll)
   ───────────────────────────────────────────────────────────────────────── */
const FinalPollChallenge = ({ selections, setSelections, onSubmit }) => {
    const toggle = (id) => {
        setSelections((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    return (
        <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Say it Out Loud!
            </Typography>
            <Typography variant="body2" sx={{ color: tokens.textSecondary, mb: 1.5 }}>
                Tell us which part of the challenge you found most useful (choose all that apply):
            </Typography>
            <Stack>
                {FINAL_POLL_OPTIONS.map((opt) => (
                    <FormControlLabel
                        key={opt.id}
                        control={<Checkbox size="small" checked={selections.includes(opt.id)} onChange={() => toggle(opt.id)} />}
                        label={<Typography variant="body2">{opt.label}</Typography>}
                    />
                ))}
            </Stack>
            <Button
                variant="contained"
                disabled={selections.length === 0}
                onClick={onSubmit}
                sx={{
                    background: tokens.gradient, borderRadius: "8px", textTransform: "none", fontWeight: 600, boxShadow: "none", mt: 1.5,
                    animation: "wcCtaPulse3 2.4s ease-in-out infinite",
                    "@keyframes wcCtaPulse3": {
                        "0%,100%": { boxShadow: "0 0 0 0 rgba(47,111,208,0.45)" },
                        "50%": { boxShadow: "0 0 0 7px rgba(47,111,208,0)" },
                    },
                }}
            >
                Submit
            </Button>
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────────────────
   Post-campaign states
   ───────────────────────────────────────────────────────────────────────── */
const WaitingCard = ({ nextDay }) => (
    <Box sx={{ maxWidth: 560, mx: "auto", px: { xs: 1, sm: 0 } }}>
        <Card elevation={0} sx={{ borderRadius: "16px", border: `1px solid ${tokens.border}`, overflow: "hidden" }}>
            <Box sx={{ height: 5, background: tokens.gradient }} />
            <Box sx={{
                background: "linear-gradient(135deg, #eef4fd 0%, #ede9fb 100%)",
                px: 3, pt: 3, pb: 2.5,
                display: "flex", alignItems: "flex-start", gap: 2,
            }}>
                <Box sx={{
                    width: 48, height: 48, borderRadius: "14px", flexShrink: 0,
                    background: tokens.gradient,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 4px 14px rgba(47,111,208,0.30)",
                }}>
                    <CheckCircle sx={{ fontSize: 26, color: "#fff" }} />
                </Box>
                <Box>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ color: "#111827", lineHeight: 1.3, mb: 0.4 }}>
                        You're all caught up! 🎉
                    </Typography>
                    <Typography variant="body2" sx={{ color: tokens.textSecondary, lineHeight: 1.5 }}>
                        Great work on today's challenge. Come back for{" "}
                        <Box component="span" sx={{ fontWeight: 700, color: tokens.primary }}>
                            {DAY_META[nextDay]?.label}
                        </Box>{" "}to keep the momentum going.
                    </Typography>
                </Box>
            </Box>
            <Divider sx={{ borderColor: tokens.border }} />
            <CardContent sx={{ px: 3, py: 2, "&:last-child": { pb: 2.5 } }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: tokens.primary, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: tokens.textSecondary, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                        Next up · {DAY_META[nextDay]?.label}
                    </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: "#374151", mt: 0.75, fontWeight: 500 }}>
                    {DAY_META[nextDay]?.short}
                </Typography>
            </CardContent>
        </Card>
    </Box>
);

const CongratsCard = () => (
    <Box sx={{ maxWidth: 560, mx: "auto", px: { xs: 1, sm: 0 } }}>
        <Card elevation={0} sx={{ borderRadius: "16px", border: `1px solid ${tokens.border}`, overflow: "hidden" }}>
            <Box sx={{ px: 2.5, pt: 2, pb: 1.5, background: tokens.gradient, color: "#fff", position: "relative", overflow: "hidden" }}>
                <Box sx={{ position: "absolute", top: -24, right: -24, width: 110, height: 110, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.07)" }} />
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                    <Box sx={{
                        width: 36, height: 36, borderRadius: "10px", flexShrink: 0,
                        bgcolor: "rgba(255,255,255,0.20)", backdropFilter: "blur(8px)",
                        border: "1px solid rgba(255,255,255,0.32)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                        <CheckCircle sx={{ fontSize: 20, color: "#fff" }} />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={800}>
                        Congratulations! 🎊
                    </Typography>
                </Stack>
                <Typography variant="body2" sx={{ opacity: 0.88, lineHeight: 1.5 }}>
                    You've completed the{" "}
                    <Box component="span" sx={{ fontWeight: 700 }}>Wellness, One Day at a Time</Box>{" "}
                    challenge series. Here's what to carry forward:
                </Typography>
            </Box>

            <CardContent sx={{ px: 2.5, pt: 1.5, pb: 2, "&:last-child": { pb: 2 } }}>
                <Typography variant="caption" sx={{
                    color: tokens.textSecondary, fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: "0.06em", display: "block", mb: 1,
                }}>
                    Remember
                </Typography>
                <Stack spacing={0.75}>
                    {REMINDERS.map((r) => (
                        <Box key={r} sx={{ display: "flex", alignItems: "baseline", gap: 1.25 }}>
                            <Box sx={{
                                width: 6, height: 6, borderRadius: "50%",
                                bgcolor: tokens.primary, flexShrink: 0,
                                position: "relative", top: "1px",
                            }} />
                            <Typography variant="body2" sx={{ color: "#374151", lineHeight: 1.5 }}>
                                {r}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            </CardContent>
        </Card>
    </Box>
);

export default WellnessCampaign;
